import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Unlock, CreditCard } from "lucide-react";
import FullAuditLeadDialog from "@/components/FullAuditLeadDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POPULAR_CATEGORIES = [
  "Smartphone",
  "Laptop",
  "Earphone/TWS",
  "Air Purifier",
  "Kipas Angin",
  "Rice Cooker",
  "Skincare",
  "Sepatu",
  "Motor",
  "Mobil",
  "Kamera",
  "Smartwatch",
];

const PREMIUM_KEY_STORAGE = "fp_premium_key";

export default function AuditForm({ onSubmit, loading, prefill }) {
  const [brand, setBrand] = useState("");
  const [model, setModel] = useState("");
  const [category, setCategory] = useState("");
  const [tier, setTier] = useState("fast");
  const [premiumKey, setPremiumKey] = useState("");
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);
  const [showLeadDialog, setShowLeadDialog] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [paying, setPaying] = useState(false);
  const [buyEmail, setBuyEmail] = useState("");
  const [mode, setMode] = useState("buy"); // buy | code | recover
  const [recoverEmail, setRecoverEmail] = useState("");
  const [recoverResult, setRecoverResult] = useState(null);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [keyStatus, setKeyStatus] = useState(null);

  useEffect(() => {
    const stored = localStorage.getItem(PREMIUM_KEY_STORAGE) || "";
    setPremiumKey(stored);
    if (stored) {
      // fetch status (days left, kind)
      axios
        .get(`${API}/payments/key-status`, { params: { key: stored }, timeout: 10000 })
        .then((r) => setKeyStatus(r.data || null))
        .catch(() => setKeyStatus(null));
    }
  }, []);

  // Prefill from query params (e.g. ?brand=...&model=...&category=...)
  useEffect(() => {
    if (!prefill) return;
    if (prefill.brand) setBrand(prefill.brand);
    if (prefill.model) setModel(prefill.model);
    if (prefill.category) setCategory(prefill.category);
    if (prefill.tier === "premium" || prefill.tier === "fast") setTier(prefill.tier);
  }, [prefill]);

  const handleTierClick = (newTier) => {
    if (newTier === "premium" && !premiumKey) {
      setShowPremiumDialog(true);
      return;
    }
    setTier(newTier);
  };

  const submitPremiumKey = () => {
    const k = keyInput.trim();
    if (!k) return;
    localStorage.setItem(PREMIUM_KEY_STORAGE, k);
    setPremiumKey(k);
    setTier("premium");
    setShowPremiumDialog(false);
    setKeyInput("");
  };

  const clearPremiumKey = () => {
    localStorage.removeItem(PREMIUM_KEY_STORAGE);
    setPremiumKey("");
    setTier("fast");
    setKeyStatus(null);
  };

  const buyPremium = async () => {
    if (!buyEmail.trim() || !buyEmail.includes("@")) {
      toast.error("Email valid wajib diisi untuk pembelian.");
      return;
    }
    setPaying(true);
    try {
      const res = await axios.post(
        `${API}/payments/checkout-session`,
        {
          package_id: "premium_30d",
          origin_url: window.location.origin,
          email: buyEmail.trim().toLowerCase(),
        },
        { timeout: 30000 }
      );
      const { url, order_id } = res.data || {};
      if (!url) throw new Error("No checkout URL");
      if (order_id) localStorage.setItem("fp_last_order_id", order_id);
      localStorage.setItem("fp_premium_email", buyEmail.trim().toLowerCase());
      window.location.href = url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Gagal memulai pembayaran.";
      toast.error(msg);
      setPaying(false);
    }
  };

  const recoverKeys = async () => {
    if (!recoverEmail.trim() || !recoverEmail.includes("@")) {
      toast.error("Email valid wajib diisi.");
      return;
    }
    setRecoverLoading(true);
    setRecoverResult(null);
    try {
      const res = await axios.post(
        `${API}/payments/recover`,
        { email: recoverEmail.trim().toLowerCase() },
        { timeout: 15000 }
      );
      setRecoverResult(res.data);
      if ((res.data?.keys || []).length === 0) {
        toast.info("Tidak ada kode aktif untuk email ini.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Gagal cek email.");
    } finally {
      setRecoverLoading(false);
    }
  };

  const activateRecoveredKey = (k) => {
    localStorage.setItem(PREMIUM_KEY_STORAGE, k);
    setPremiumKey(k);
    setTier("premium");
    setShowPremiumDialog(false);
    setRecoverResult(null);
    setRecoverEmail("");
    setMode("buy");
    toast.success("Kode premium diaktifkan.");
    // Refresh status badge
    axios
      .get(`${API}/payments/key-status`, { params: { key: k }, timeout: 10000 })
      .then((r) => setKeyStatus(r.data || null))
      .catch(() => {});
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!brand.trim() || !model.trim()) return;
    onSubmit({
      brand: brand.trim(),
      model: model.trim(),
      category: category.trim() || null,
      tier,
      premiumKey: tier === "premium" ? premiumKey : null,
    });
  };

  const handleReset = () => {
    setBrand("");
    setModel("");
    setCategory("");
    // Clear URL query params so prefill doesn't re-trigger on reload
    if (typeof window !== "undefined" && window.history && window.location.search) {
      const cleanUrl = window.location.pathname + window.location.hash;
      window.history.replaceState({}, "", cleanUrl);
    }
    toast.success("Form audit di-reset.");
  };

  return (
    <>
    <form
      onSubmit={handleSubmit}
      className="bg-white border-2 border-black p-6 md:p-8 shadow-brutal"
      data-testid="audit-form"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#666666] border-b-2 border-black pb-2 flex justify-between">
        <span>FORM // AUDIT REQUEST</span>
        <span>REQUIRED: BRAND · MODEL</span>
      </div>

      <div className="grid md:grid-cols-2 gap-0 mt-6 border-2 border-black">
        <FieldBlock label="Merek" required subtext="Contoh: Xiaomi, Samsung, Sharp" rightBorder bottomBorder>
          <input
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            placeholder="Tulis merek..."
            className="h-14 w-full px-4 bg-transparent font-display font-bold text-lg focus:outline-none focus:bg-yellow-50"
            data-testid="input-brand"
            required
          />
        </FieldBlock>
        <FieldBlock label="Model / Tipe" required subtext="Contoh: Redmi Note 13, S24 Ultra" bottomBorder>
          <input
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="Tulis model..."
            className="h-14 w-full px-4 bg-transparent font-display font-bold text-lg focus:outline-none focus:bg-yellow-50"
            data-testid="input-model"
            required
          />
        </FieldBlock>
        <FieldBlock label="Kategori (opsional)" subtext="Bantu PI verifikasi konteks produk" fullSpan>
          <div className="px-4 pb-4 pt-2">
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="Tulis atau pilih dari rekomendasi"
              className="h-12 w-full px-3 bg-white border-2 border-black font-sans text-base focus:outline-none focus:bg-yellow-50"
              data-testid="input-category"
              list="category-suggestions"
            />
            <datalist id="category-suggestions">
              {POPULAR_CATEGORIES.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <div className="mt-3 flex flex-wrap gap-2">
              {POPULAR_CATEGORIES.map((c) => (
                <button
                  type="button"
                  key={c}
                  onClick={() => setCategory(c)}
                  className={`font-mono text-[11px] uppercase tracking-[0.14em] px-2.5 py-1.5 border-2 border-black hover:bg-yellow-200 transition-colors ${
                    category === c ? "bg-black text-white" : "bg-white"
                  }`}
                  data-testid={`chip-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </FieldBlock>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em]">
          <span className="text-[#666]">Mode:</span>
          <button
            type="button"
            onClick={() => handleTierClick("fast")}
            className={`px-3 py-1.5 border-2 border-black ${tier === "fast" ? "bg-black text-[#FFD700]" : "bg-white hover:bg-yellow-50"}`}
            data-testid="tier-fast-button"
            title="Claude Haiku 4.5 — hemat kredit ~3x"
          >
            ⚡ BASIC AUDIT · GRATIS
          </button>
          <button
            type="button"
            onClick={() => handleTierClick("premium")}
            className={`px-3 py-1.5 border-2 border-black inline-flex items-center gap-1.5 ${tier === "premium" ? "bg-black text-[#FFD700]" : "bg-white hover:bg-yellow-50"}`}
            data-testid="tier-premium-button"
            title={premiumKey ? "Premium aktif" : "Full Audit — Rp 19.900 / 24 jam"}
          >
            {premiumKey ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
            ◆ FULL AUDIT
            {!premiumKey && <span className="text-[#FF2400]">· BERBAYAR</span>}
          </button>
          {premiumKey && (
            <>
              {keyStatus && keyStatus.kind === "paid" && typeof keyStatus.days_left === "number" && (
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 border-2 border-black ${keyStatus.days_left <= 3 ? "bg-[#FF2400] text-white" : keyStatus.days_left <= 7 ? "bg-[#FFD700] text-black" : "bg-[#002FA7] text-white"}`}
                  data-testid="premium-days-left"
                  title={`Berakhir: ${keyStatus.expires_at}`}
                >
                  {keyStatus.days_left}D
                </span>
              )}
              <button
                type="button"
                onClick={clearPremiumKey}
                className="px-2 py-1.5 border-2 border-black bg-white text-[10px] hover:bg-[#FF2400] hover:text-white"
                data-testid="clear-premium-key"
                title="Hapus kode premium"
              >
                ✕
              </button>
            </>
          )}
        </div>
        <button
          type="submit"
          disabled={loading || !brand.trim() || !model.trim()}
          className="bg-black text-white px-8 py-4 font-mono font-bold uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
          data-testid="submit-audit-button"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Mengaudit...
            </>
          ) : (
            <>▶ Jalankan Audit</>
          )}
        </button>
      </div>

      {/* RESET FORM */}
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={handleReset}
          disabled={loading || (!brand && !model && !category)}
          className="inline-flex items-center gap-2 border-2 border-black bg-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] hover:bg-[#FF2400] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="reset-form-button"
          title="Kosongkan semua field input"
        >
          ↺ Reset Form Audit
        </button>
      </div>

      {showPremiumDialog && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="premium-dialog">
          <div className="bg-white border-2 border-black max-w-md w-full p-6 shadow-brutal max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#666]">Akses Premium</div>
                <h3 className="font-display font-black uppercase tracking-tighter text-2xl mt-1">
                  ◆ Premium · Analisis Mendalam &amp; Kontekstual
                </h3>
              </div>
              <button
                type="button"
                onClick={() => { setShowPremiumDialog(false); setKeyInput(""); }}
                className="border-2 border-black px-2 py-1 hover:bg-[#FF2400] hover:text-white font-mono text-sm"
                data-testid="close-premium-dialog"
              >
                ✕
              </button>
            </div>

            {/* TAB SELECTOR */}
            <div className="mt-4 grid grid-cols-3 border-2 border-black font-mono text-[10px] uppercase tracking-[0.18em]">
              <button
                type="button"
                onClick={() => setMode("buy")}
                className={`py-2 border-r-2 border-black ${mode === "buy" ? "bg-black text-white" : "bg-white hover:bg-yellow-50"}`}
                data-testid="tab-buy"
              >Beli</button>
              <button
                type="button"
                onClick={() => setMode("recover")}
                className={`py-2 border-r-2 border-black ${mode === "recover" ? "bg-black text-white" : "bg-white hover:bg-yellow-50"}`}
                data-testid="tab-recover"
              >Recover</button>
              <button
                type="button"
                onClick={() => setMode("code")}
                className={`py-2 ${mode === "code" ? "bg-black text-white" : "bg-white hover:bg-yellow-50"}`}
                data-testid="tab-code"
              >Punya Kode</button>
            </div>

            {mode === "buy" && (
              <>
                <ul className="mt-4 text-xs font-mono uppercase tracking-[0.14em] space-y-1 text-[#444]">
                  <li>· Analisis 4–5x lebih mendalam</li>
                  <li>· Pemahaman konteks pasar Indonesia lebih kuat</li>
                  <li>· Prioritas antrian saat traffic tinggi</li>
                </ul>
                <div className="mt-4 border-2 border-black p-4 bg-[#FFD700]">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em]">Paket 24 Jam</div>
                  <div className="mt-1 font-display font-black uppercase tracking-tighter text-3xl">Rp 19.900</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#333]">
                    Sekali bayar · Akses 24 jam · Midtrans
                  </div>

                  <label className="mt-4 block font-mono text-[10px] uppercase tracking-[0.2em]">
                    Email (wajib — untuk recover kode)
                  </label>
                  <input
                    type="email"
                    value={buyEmail}
                    onChange={(e) => setBuyEmail(e.target.value)}
                    placeholder="nama@email.com"
                    className="mt-1.5 h-11 w-full px-3 bg-white border-2 border-black font-mono text-sm focus:outline-none"
                    data-testid="buy-email-input"
                    required
                  />

                  <button
                    type="button"
                    onClick={buyPremium}
                    disabled={paying || !buyEmail.trim()}
                    className="mt-3 w-full bg-black text-[#FFD700] px-4 py-3 font-mono font-bold uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7] hover:text-white disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    data-testid="buy-premium-button"
                  >
                    {paying ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Membuka Midtrans...</>
                    ) : (
                      <><CreditCard className="w-4 h-4" /> Bayar via Midtrans →</>
                    )}
                  </button>
                  <div className="mt-2 font-mono text-[9px] uppercase tracking-[0.18em] text-[#555]">
                    GoPay · OVO · DANA · ShopeePay · QRIS · Bank Transfer · Kartu
                  </div>
                </div>
              </>
            )}

            {mode === "recover" && (
              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  Email yang dipakai saat membeli
                </label>
                <input
                  type="email"
                  value={recoverEmail}
                  onChange={(e) => setRecoverEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && recoverKeys()}
                  placeholder="nama@email.com"
                  className="mt-2 h-12 w-full px-3 bg-white border-2 border-black font-mono text-base focus:outline-none focus:bg-yellow-50"
                  data-testid="recover-email-input"
                />
                <button
                  type="button"
                  onClick={recoverKeys}
                  disabled={recoverLoading || !recoverEmail.trim()}
                  className="mt-3 w-full bg-black text-white px-4 py-3 font-mono font-bold uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                  data-testid="recover-submit-button"
                >
                  {recoverLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> Mencari...</> : "Cari Kode Saya"}
                </button>

                {recoverResult && (
                  <div className="mt-4 space-y-2" data-testid="recover-results">
                    {(recoverResult.keys || []).length === 0 ? (
                      <div className="border-2 border-black bg-[#fff4f1] p-3 font-mono text-xs uppercase tracking-[0.14em] text-[#FF2400]">
                        Tidak ada kode aktif untuk email ini.
                      </div>
                    ) : (
                      (recoverResult.keys || []).map((k, i) => (
                        <div key={i} className="border-2 border-black p-3 bg-white">
                          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#666]">
                            Kode aktif · Berlaku hingga {new Date(k.expires_at).toLocaleDateString("id-ID")}
                          </div>
                          <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                            <code className="font-mono font-bold text-lg break-all">{k.key}</code>
                            <button
                              type="button"
                              onClick={() => activateRecoveredKey(k.key)}
                              className="bg-black text-white px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7]"
                              data-testid={`use-recovered-key-${i}`}
                            >
                              Aktifkan
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {mode === "code" && (
              <div className="mt-4">
                <label className="font-mono text-[10px] uppercase tracking-[0.2em]">
                  Sudah punya kode? Masukkan di sini
                </label>
                <input
                  type="password"
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && submitPremiumKey()}
                  placeholder="FP-XXXXXXXXXXXXXX"
                  className="mt-2 h-12 w-full px-3 bg-white border-2 border-black font-mono text-base focus:outline-none focus:bg-yellow-50"
                  data-testid="premium-key-input"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={submitPremiumKey}
                    disabled={!keyInput.trim()}
                    className="bg-black text-white px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.18em] border-2 border-black hover:bg-[#002FA7] disabled:opacity-50"
                    data-testid="unlock-premium-button"
                  >
                    ◆ Unlock dengan Kode
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </form>

    <FullAuditLeadDialog
      open={showLeadDialog}
      onClose={() => setShowLeadDialog(false)}
      product={[brand.trim(), model.trim(), category.trim() && `(${category.trim()})`].filter(Boolean).join(" ")}
    />
    </>
  );
}

function FieldBlock({ label, subtext, required, rightBorder, bottomBorder, fullSpan, children }) {
  return (
    <div
      className={[
        fullSpan ? "md:col-span-2" : "",
        rightBorder ? "md:border-r-2 md:border-black" : "",
        bottomBorder ? "border-b-2 border-black" : "",
      ].join(" ")}
    >
      <div className="px-4 pt-4">
        <div className="flex items-baseline justify-between">
          <label className="font-mono text-[10px] uppercase tracking-[0.2em]">
            {label} {required && <span className="text-[#FF2400]">*</span>}
          </label>
          {subtext && (
            <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#666666] hidden md:inline">
              {subtext}
            </span>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
